import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { getThreatMap } from '../api/threat-map.js';
import { countryName } from '../lib/country.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';
import { Flag } from './Flag.js';
import { GeoAttribution } from './GeoAttribution.js';

/**
 * Where attacks come from (P11.11): a map and, beside it, the countries ranked.
 *
 * The countries are the ones CrowdSec resolved when it raised an alert; Bartizo has
 * no GeoIP database of its own, so traffic from addresses CrowdSec never alerted on
 * is not on the map, and the card says how many events it could not place.
 */

const WorldMap = lazy(() => import('./WorldMap.js'));

const RANGES = [
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
] as const;

const count = (value: number): string => value.toLocaleString('en-US');

export function ThreatMapCard({ serverId }: { serverId?: string }): ReactNode {
  const [hours, setHours] = useState('24');
  const [selected, setSelected] = useState<string | undefined>();
  const map = useQuery({
    queryKey: ['threat-map', hours, serverId ?? null],
    queryFn: () => getThreatMap(Number(hours), serverId),
    refetchInterval: 60_000,
  });
  const countries = map.data?.countries ?? [];
  const peak = Math.max(1, ...countries.map((each) => each.events));

  return (
    <Card
      title="Where attacks come from"
      description="Events by the country of their source address: CrowdSec's own lookup where it raised the alert, DB-IP's otherwise."
      aside={
        <SelectField
          label="Range"
          value={hours}
          onChange={(event) => {
            setHours(event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      }
    >
      {map.isError && (
        <Alert tone="danger" title="Could not load the map">
          Try again in a moment.
        </Alert>
      )}
      {map.isPending && (
        <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading the map" />
      )}
      {map.data !== undefined && countries.length === 0 && (
        <p className="muted text-sm">
          No event in this range came from an address CrowdSec has placed in a country.
        </p>
      )}
      {map.data !== undefined && countries.length > 0 && (
        <div className="map-layout">
          <Suspense
            fallback={
              <div
                className="skeleton skeleton-block"
                aria-busy="true"
                aria-label="Loading the map"
              />
            }
          >
            <WorldMap values={countries} selected={selected} onSelect={setSelected} />
          </Suspense>
          <ol className="country-list" aria-label="Countries, busiest first">
            {countries.slice(0, 8).map((each) => (
              <li key={each.code}>
                <button
                  type="button"
                  className="country-row"
                  aria-pressed={selected === each.code}
                  onClick={() => {
                    setSelected(selected === each.code ? undefined : each.code);
                  }}
                  title={countryName(each.code)}
                >
                  <Flag code={each.code} name />
                  <span className="country-bar" aria-hidden="true">
                    <span
                      className={each.high > 0 ? 'is-hot' : undefined}
                      style={{ width: `${String(Math.round((each.events / peak) * 100))}%` }}
                    />
                  </span>
                  <span className="country-count">{count(each.events)}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}
      {map.data !== undefined && countries.length > 0 && <GeoAttribution />}
      {map.data !== undefined && map.data.unplaced.events > 0 && (
        <p className="muted text-sm">
          {count(map.data.unplaced.events)} events from {count(map.data.unplaced.addresses)}{' '}
          addresses have no country: neither CrowdSec nor DB-IP placed them.
        </p>
      )}
    </Card>
  );
}
